import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Send, MessageCircle, ExternalLink, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (url, opts = {}) => {
  const token = localStorage.getItem("token");
  return fetch(`${API}${url}`, { ...opts, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) }});
};

const COLOR_BADGE = {
  green: { bg: "bg-emerald-100", text: "text-emerald-800", label: "🟢 VERDE" },
  yellow: { bg: "bg-amber-100", text: "text-amber-800", label: "🟡 GIALLO" },
  orange: { bg: "bg-orange-100", text: "text-orange-800", label: "🟠 ARANCIONE" },
  red: { bg: "bg-red-100", text: "text-red-800", label: "🔴 ROSSO" },
};

export default function CrmInvioMiratoPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [minGiorni, setMinGiorni] = useState(40);
  const [colorFilter, setColorFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await auth(`/crm/promemoria-visita/candidates?min_giorni=${minGiorni}&only_color=${colorFilter}`);
    setLoading(false);
    if (r.ok) {
      const d = await r.json();
      setItems(d.items || []);
      setSelected(new Set());
    }
  }, [minGiorni, colorFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((it) => !agentFilter || (it.agente_email || "").includes(agentFilter.toLowerCase()) || (it.agente_name || "").toLowerCase().includes(agentFilter.toLowerCase()));

  const toggleOne = (cid) => {
    const next = new Set(selected);
    if (next.has(cid)) next.delete(cid);
    else next.add(cid);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(filtered.map((x) => x.cliente_id)));
  const selectNone = () => setSelected(new Set());
  const selectRedOnly = () => setSelected(new Set(filtered.filter((x) => x.colore === "red").map((x) => x.cliente_id)));

  const send = async () => {
    if (!selected.size) { toast.error("Seleziona almeno un cliente"); return; }
    if (!window.confirm(`Inviare promemoria per ${selected.size} cliente/i agli agenti corrispondenti? Riceveranno email + link WhatsApp.`)) return;
    setSending(true);
    const r = await auth("/crm/promemoria-visita/send-mirato", { method: "POST", body: JSON.stringify({ cliente_ids: Array.from(selected) })});
    setSending(false);
    if (r.ok) {
      const d = await r.json();
      setLastResult(d);
      toast.success(`📧 ${d.sent}/${d.total} promemoria inviati${d.errors.length ? ` (${d.errors.length} errori)` : ""}`);
      setSelected(new Set());
      load();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Errore invio");
    }
  };

  const counts = {
    green: filtered.filter((x) => x.colore === "green").length,
    yellow: filtered.filter((x) => x.colore === "yellow").length,
    orange: filtered.filter((x) => x.colore === "orange").length,
    red: filtered.filter((x) => x.colore === "red").length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/admin" className="text-[#7A4856] hover:underline text-sm inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        </div>
        <h1 className="text-3xl font-bold text-[#5A3A3A] mb-2">📨 Invio mirato promemoria</h1>
        <p className="text-sm text-gray-600 mb-4">Seleziona uno o più clienti in ritardo e invia subito un promemoria (email + WhatsApp) ai rispettivi agenti.</p>

        {/* Filtri */}
        <div className="bg-white rounded-2xl border border-rose-100 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Giorni minimi inattività</label>
              <select value={minGiorni} onChange={(e) => setMinGiorni(parseInt(e.target.value))} className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm" data-testid="filter-min-giorni">
                <option value="0">Tutti (anche &lt; 20gg)</option>
                <option value="20">20+ giorni</option>
                <option value="40">40+ giorni</option>
                <option value="60">60+ giorni</option>
                <option value="90">90+ giorni</option>
                <option value="180">180+ giorni</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Colore</label>
              <select value={colorFilter} onChange={(e) => setColorFilter(e.target.value)} className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm" data-testid="filter-color">
                <option value="all">Tutti i colori</option>
                <option value="red">🔴 Solo rossi</option>
                <option value="orange">🟠 Solo arancioni</option>
                <option value="yellow">🟡 Solo gialli</option>
                <option value="green">🟢 Solo verdi</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Cerca per agente (nome o email)</label>
              <input value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)} placeholder="es. distributore.veneto" className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm" data-testid="filter-agente" />
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-rose-100 flex-wrap gap-2">
            <div className="flex gap-2 flex-wrap text-xs">
              <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-bold">🔴 {counts.red}</span>
              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full font-bold">🟠 {counts.orange}</span>
              <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full font-bold">🟡 {counts.yellow}</span>
              <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full font-bold">🟢 {counts.green}</span>
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full font-bold">TOT: {filtered.length}</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={selectAll} className="px-3 py-1 text-xs bg-rose-100 text-rose-800 rounded-full" data-testid="btn-select-all">✓ Tutti</button>
              <button onClick={selectRedOnly} className="px-3 py-1 text-xs bg-red-100 text-red-800 rounded-full" data-testid="btn-select-red">🔴 Solo rossi</button>
              <button onClick={selectNone} className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full" data-testid="btn-select-none">✗ Nessuno</button>
              <button onClick={load} className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full" data-testid="btn-reload"><RefreshCw className="w-3 h-3 inline" /> Ricarica</button>
            </div>
          </div>
        </div>

        {/* Lista clienti */}
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 mx-auto animate-spin text-rose-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-rose-100 p-8 text-center text-gray-500">Nessun cliente trovato con i filtri correnti.</div>
        ) : (
          <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden mb-4" data-testid="candidates-list">
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-rose-50 text-left sticky top-0 z-10">
                  <tr>
                    <th className="p-3 w-10"><input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={(e) => e.target.checked ? selectAll() : selectNone()} className="w-4 h-4 accent-rose-600" /></th>
                    <th className="p-3 text-xs font-bold text-rose-900 uppercase">Cliente</th>
                    <th className="p-3 text-xs font-bold text-rose-900 uppercase">Agente</th>
                    <th className="p-3 text-xs font-bold text-rose-900 uppercase text-center">Giorni</th>
                    <th className="p-3 text-xs font-bold text-rose-900 uppercase">Ultimo ordine</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => {
                    const b = COLOR_BADGE[it.colore] || COLOR_BADGE.green;
                    const isSelected = selected.has(it.cliente_id);
                    return (
                      <tr key={it.cliente_id} className={`border-b border-rose-50 hover:bg-rose-25 ${isSelected ? "bg-rose-50" : ""}`} onClick={() => toggleOne(it.cliente_id)} data-testid={`row-${it.cliente_id}`}>
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={isSelected} onChange={() => toggleOne(it.cliente_id)} className="w-4 h-4 accent-rose-600" />
                        </td>
                        <td className="p-3">
                          <p className="font-semibold text-[#5A3A3A]">{it.ragione_sociale}</p>
                          <p className="text-xs text-gray-500">{it.citta} {it.provincia && `(${it.provincia})`}</p>
                        </td>
                        <td className="p-3">
                          {it.agente_email ? (
                            <>
                              <p className="text-sm">{it.agente_name || it.agente_email.split("@")[0]}</p>
                              <p className="text-xs text-gray-500">{it.agente_email}</p>
                              {it.agente_opted_out && <span className="text-[10px] text-red-600">🔕 Opted-out</span>}
                              {!it.agente_phone && <span className="text-[10px] text-orange-600">📵 No tel</span>}
                            </>
                          ) : (
                            <span className="text-xs text-red-600">⚠️ Nessun agente</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${b.bg} ${b.text}`}>{it.giorni_inattivo}gg</span>
                        </td>
                        <td className="p-3 text-xs text-gray-600">
                          <p>{it.ultimo_ordine_data}</p>
                          <p>€ {(it.ultimo_ordine_importo || 0).toFixed(2)}</p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pulsante invio */}
        <div className="sticky bottom-4 bg-white rounded-2xl border-2 border-rose-300 p-4 shadow-lg flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-[#5A3A3A]"><b>{selected.size}</b> cliente/i selezionati → invio a <b>{new Set(filtered.filter((x) => selected.has(x.cliente_id)).map((x) => x.agente_email)).size}</b> agente/i diversi</p>
          <button onClick={send} disabled={!selected.size || sending} className="px-6 py-3 bg-gradient-to-r from-rose-500 to-rose-700 text-white rounded-full font-bold disabled:opacity-50 hover:opacity-90" data-testid="btn-send-mirato">
            {sending ? <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> : <Send className="w-4 h-4 inline mr-2" />}
            📨 Invia promemoria ({selected.size})
          </button>
        </div>

        {/* Risultati ultimo invio */}
        {lastResult && (
          <div className="bg-white rounded-2xl border border-rose-100 p-4 mt-4" data-testid="last-result">
            <h3 className="font-bold text-[#5A3A3A] mb-2">📋 Ultimo invio: {lastResult.sent}/{lastResult.total} OK</h3>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {lastResult.details && lastResult.details.map((d, i) => (
                <div key={i} className="flex items-center gap-2 text-xs p-2 bg-rose-25 rounded">
                  <span>{d.ok ? "✅" : "❌"}</span>
                  <span className="flex-1 truncate"><b>{d.rs}</b> → {d.agente} ({d.giorni}gg)</span>
                  {d.wa_url && (
                    <a href={d.wa_url} target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-green-600 text-white rounded-full hover:bg-green-700">
                      <MessageCircle className="w-3 h-3 inline" /> WA <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  )}
                </div>
              ))}
              {lastResult.errors && lastResult.errors.map((e, i) => (
                <div key={`e${i}`} className="flex items-center gap-2 text-xs p-2 bg-red-50 rounded text-red-700">
                  <AlertCircle className="w-3 h-3" />
                  <span>{e.rs || e.cliente_id}: {e.error}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
